import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { 
  Crosshair, Shield, AlertTriangle, Plane, ChevronRight,
  Target, Flag, ArrowRight, Menu, X
} from "lucide-react";
import { Button } from "../components/ui/button";

// Helicopter/Aviation icon component
const HelicopterIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 4V2" />
    <path d="M5 10H19" />
    <path d="M4 10L5 17H19L20 10" />
    <path d="M12 10V17" />
    <path d="M8 17L6 22" />
    <path d="M16 17L18 22" />
    <path d="M2 4H22" />
  </svg>
);

export default function AboutPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Check if user is logged in
  const [isLoggedIn] = useState(() => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    return !!token;
  });

  // Enable scrolling on this page
  useEffect(() => {
    document.body.classList.add("marketing-page");
    return () => {
      document.body.classList.remove("marketing-page");
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-y-auto overflow-x-hidden">
      {/* Subtle topo map background */}
      <div className="fixed inset-0 opacity-[0.03] pointer-events-none" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10 10 Q 30 5, 50 10 T 90 10' fill='none' stroke='%2310b981' stroke-width='0.5'/%3E%3Cpath d='M10 30 Q 30 25, 50 30 T 90 30' fill='none' stroke='%2310b981' stroke-width='0.5'/%3E%3Cpath d='M10 50 Q 30 45, 50 50 T 90 50' fill='none' stroke='%2310b981' stroke-width='0.5'/%3E%3Cpath d='M10 70 Q 30 65, 50 70 T 90 70' fill='none' stroke='%2310b981' stroke-width='0.5'/%3E%3Cpath d='M10 90 Q 30 85, 50 90 T 90 90' fill='none' stroke='%2310b981' stroke-width='0.5'/%3E%3C/svg%3E")`,
        backgroundSize: '200px 200px'
      }} />

      {/* Navigation - Synchronized with Marketing Page */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-lg border-b border-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo - Links to Dashboard */}
            <Link to="/dashboard" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 border border-emerald-500/50 flex items-center justify-center">
                <Crosshair className="h-5 w-5 text-emerald-400" />
              </div>
              <div>
                <span className="text-lg font-bold tracking-tight">CONTRACTOR</span>
                <span className="text-lg font-bold tracking-tight text-emerald-400"> OVERWATCH</span>
              </div>
            </Link>

            {/* Desktop Nav - Matches Marketing Page */}
            <div className="hidden md:flex items-center gap-8">
              <Link to="/marketing" className="text-sm text-slate-300 hover:text-emerald-400 transition-colors">Home</Link>
              <a href="/marketing#features" className="text-sm text-slate-300 hover:text-emerald-400 transition-colors">Features</a>
              <a href="/marketing#benefits" className="text-sm text-slate-300 hover:text-emerald-400 transition-colors">Benefits</a>
              <a href="/marketing#testimonials" className="text-sm text-slate-300 hover:text-emerald-400 transition-colors">Reviews</a>
              <Link to="/about" className="text-sm text-emerald-400 font-semibold border-b border-emerald-400 pb-0.5">About</Link>
              <Link to={isLoggedIn ? "/dashboard" : "/login"}>
                <Button 
                  variant={isLoggedIn ? "default" : "outline"} 
                  size="sm" 
                  className={isLoggedIn 
                    ? "bg-emerald-600 hover:bg-emerald-500 text-white font-bold" 
                    : "border-slate-600 text-slate-300 hover:bg-slate-800"
                  }
                >
                  {isLoggedIn ? "OPEN APP" : "Sign In"}
                </Button>
              </Link>
              {!isLoggedIn && (
                <a href="/marketing#request-access">
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold">
                    REQUEST ACCESS
                  </Button>
                </a>
              )}
            </div>

            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2 text-slate-300"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu - Matches Marketing Page */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-slate-900 border-t border-slate-800">
            <div className="px-4 py-4 space-y-3">
              <Link to="/marketing" className="block text-slate-300 hover:text-emerald-400">Home</Link>
              <a href="/marketing#features" className="block text-slate-300 hover:text-emerald-400">Features</a>
              <a href="/marketing#benefits" className="block text-slate-300 hover:text-emerald-400">Benefits</a>
              <a href="/marketing#testimonials" className="block text-slate-300 hover:text-emerald-400">Reviews</a>
              <Link to="/about" className="block text-emerald-400 font-semibold">About</Link>
              <Link to={isLoggedIn ? "/dashboard" : "/login"} className="block text-slate-300 hover:text-emerald-400">
                {isLoggedIn ? "OPEN APP" : "Sign In"}
              </Link>
              {!isLoggedIn && (
                <a href="/marketing#request-access">
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-500 mt-2">REQUEST ACCESS</Button>
                </a>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center justify-center pt-16 overflow-hidden">
        {/* Background Image - 160th SOAR Blackhawk */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_buildtech-assist/artifacts/m7yw4d9v_image.png')`,
          }}
        />
        
        {/* Dark overlay for readability */}
        <div className="absolute inset-0 bg-slate-950/70" />
        
        {/* Night vision grain overlay */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }} />
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/50 via-transparent to-slate-950" />

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Classified Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded border border-red-500/50 bg-red-500/10 mb-8">
            <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            <span className="text-xs font-mono font-bold text-red-400 tracking-widest">DECLASSIFIED DOSSIER</span>
          </div>

          {/* Main Headline - Typewriter style */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-6 leading-none" style={{ fontFamily: "'Courier New', Courier, monospace" }}>
            <span className="block text-slate-200">FROM SPECIAL OPS</span>
            <span className="block text-emerald-400 mt-2">TO GENERAL CONTRACTING.</span>
          </h1>

          {/* Sub-headline */}
          <p className="text-xl sm:text-2xl text-slate-400 font-mono tracking-wide">
            Precision. Accountability. <span className="text-white">No Excuses.</span>
          </p>

          {/* Decorative line */}
          <div className="mt-12 flex items-center justify-center gap-4">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-emerald-500/50" />
            <Crosshair className="h-6 w-6 text-emerald-500" />
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-emerald-500/50" />
          </div>
        </div>
      </section>

      {/* Timeline / Narrative Sections */}
      <section className="py-16 relative">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Timeline line */}
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-emerald-500/0 via-emerald-500/30 to-emerald-500/0 hidden lg:block" />

          {/* SECTION 1: THE REGIMENT */}
          <div className="relative mb-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left - Content */}
              <div className="lg:pr-16">
                {/* Section Badge */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                    <Plane className="h-6 w-6" />
                  </div>
                  <div>
                    <span className="text-xs text-slate-500 font-mono uppercase tracking-widest block">Section 01</span>
                    <h2 className="text-2xl font-bold text-white" style={{ fontFamily: "'Courier New', Courier, monospace" }}>THE REGIMENT</h2>
                  </div>
                </div>

                {/* 160th SOAR Badge */}
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded bg-slate-800 border border-slate-700 mb-6">
                  <Flag className="h-4 w-4 text-red-500" />
                  <span className="text-xs font-semibold text-slate-300">160th Special Operations Aviation Regiment (Airborne)</span>
                </div>

                <div className="space-y-4 text-slate-300 leading-relaxed">
                  <p>
                    I served with the <span className="text-emerald-400 font-semibold">160th Special Operations Aviation Regiment (Airborne)</span> — 
                    the Night Stalkers. In that world, precision and accountability were not optional. They were the difference 
                    between mission success and catastrophic failure.
                  </p>
                  <p>
                    Every flight plan was reviewed. Every asset was tracked. Every team member knew their role, their equipment, 
                    and their exit strategy. There was no room for guesswork, no tolerance for &ldquo;we&apos;ll figure it out later.&rdquo;
                  </p>
                  <p className="text-slate-400 italic border-l-2 border-emerald-500/50 pl-4">
                    &ldquo;Night Stalkers Don&apos;t Quit.&rdquo; — That mentality doesn&apos;t leave you when you hang up the uniform.
                  </p>
                </div>
              </div>

              {/* Right - Visual */}
              <div className="relative">
                <div className="absolute -inset-4 bg-emerald-500/5 rounded-2xl blur-xl" />
                <div className="relative bg-slate-900/80 border border-slate-800 rounded-2xl p-4 backdrop-blur">
                  <div className="aspect-video bg-slate-950 rounded-lg overflow-hidden border border-emerald-500/30 relative">
                    {/* Night Stalker helicopter image */}
                    <img 
                      src="https://customer-assets.emergentagent.com/job_buildtech-assist/artifacts/m7yw4d9v_image.png"
                      alt="160th SOAR Night Stalkers - Blackhawk helicopter night operation"
                      className="w-full h-full object-cover"
                    />
                    {/* Night vision scanline effect */}
                    <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/5 via-transparent to-emerald-500/10 pointer-events-none" />
                    {/* Corner brackets overlay */}
                    <div className="absolute top-2 left-2 w-4 h-4 border-l-2 border-t-2 border-emerald-500/50" />
                    <div className="absolute top-2 right-2 w-4 h-4 border-r-2 border-t-2 border-emerald-500/50" />
                    <div className="absolute bottom-2 left-2 w-4 h-4 border-l-2 border-b-2 border-emerald-500/50" />
                    <div className="absolute bottom-2 right-2 w-4 h-4 border-r-2 border-b-2 border-emerald-500/50" />
                  </div>
                  <div className="mt-4 flex items-center justify-between text-xs text-slate-500 font-mono">
                    <span className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                      160TH SOAR &ldquo;NIGHT STALKERS&rdquo;
                    </span>
                    <span>STATUS: HONORABLY DISCHARGED</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* SECTION 2: THE DISCONNECT */}
          <div className="relative mb-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left - Visual (reversed order on desktop) */}
              <div className="relative order-2 lg:order-1">
                <div className="absolute -inset-4 bg-amber-500/5 rounded-2xl blur-xl" />
                <div className="relative bg-slate-900/80 border border-slate-800 rounded-2xl p-8 backdrop-blur">
                  <div className="space-y-3">
                    {/* Problem bullets */}
                    {[
                      "Estimating tools don't talk to PM tools",
                      "Proposals live in one place, invoices in another",
                      "Subs see pricing they shouldn't",
                      "Leads fall through the cracks",
                      "No single source of truth"
                    ].map((problem, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 bg-red-500/5 border border-red-500/20 rounded-lg">
                        <AlertTriangle className="h-4 w-4 text-red-400 flex-shrink-0" />
                        <span className="text-sm text-slate-300">{problem}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 text-xs text-slate-500 font-mono text-center">
                    STATUS: <span className="text-red-400">UNACCEPTABLE</span>
                  </div>
                </div>
              </div>

              {/* Right - Content */}
              <div className="lg:pl-16 order-1 lg:order-2">
                {/* Section Badge */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400">
                    <AlertTriangle className="h-6 w-6" />
                  </div>
                  <div>
                    <span className="text-xs text-slate-500 font-mono uppercase tracking-widest block">Section 02</span>
                    <h2 className="text-2xl font-bold text-white" style={{ fontFamily: "'Courier New', Courier, monospace" }}>THE DISCONNECT</h2>
                  </div>
                </div>

                <div className="space-y-4 text-slate-300 leading-relaxed">
                  <p>
                    Transitioning to construction, I was <span className="text-amber-400 font-semibold">shocked</span>. 
                    The industry that builds America&apos;s homes and businesses was running on spreadsheets, sticky notes, and prayer.
                  </p>
                  <p>
                    Estimating tools don&apos;t talk to project management tools. Proposals live in one system, invoices in another. 
                    Subcontractors see pricing they have no business seeing. Leads fall through the cracks because there&apos;s 
                    no unified pipeline.
                  </p>
                  <p>
                    Software companies build tools <span className="text-white font-semibold">around</span> contractors, 
                    not <span className="text-white font-semibold">for</span> them. They don&apos;t understand the chaos of the job site, 
                    the pressure of thin margins, or the reality of managing a crew that doesn&apos;t sit behind desks.
                  </p>
                  <p className="text-amber-400 font-semibold">
                    This wasn&apos;t just inefficient. It was a mission-critical failure waiting to happen.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* SECTION 3: THE SOLUTION */}
          <div className="relative">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left - Content */}
              <div className="lg:pr-16">
                {/* Section Badge */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                    <Shield className="h-6 w-6" />
                  </div>
                  <div>
                    <span className="text-xs text-slate-500 font-mono uppercase tracking-widest block">Section 03</span>
                    <h2 className="text-2xl font-bold text-white" style={{ fontFamily: "'Courier New', Courier, monospace" }}>THE SOLUTION</h2>
                  </div>
                </div>

                <div className="space-y-4 text-slate-300 leading-relaxed">
                  <p>
                    <span className="text-emerald-400 font-semibold">Contractor Overwatch</span> is not just another app. 
                    It is a <span className="text-white font-semibold">unified operating system</span> — designed like a Special Ops mission.
                  </p>
                  <p>
                    <span className="text-white">Clarity:</span> Every lead, estimate, proposal, and invoice in one place. 
                    One login. One dashboard. One source of truth.
                  </p>
                  <p>
                    <span className="text-white">Redundancy:</span> Nothing falls through the cracks. Visual decay on stale leads. 
                    Automated follow-ups. Compliance tracking that locks out expired subs.
                  </p>
                  <p>
                    <span className="text-white">Accountability:</span> Know exactly where every dollar goes. See who&apos;s on site and when. 
                    Generate reports that would make a battalion commander proud.
                  </p>
                  <p className="text-emerald-400 font-semibold border-l-2 border-emerald-500/50 pl-4 italic">
                    I built the system I wish I had when I transitioned. Now I&apos;m sharing it with every contractor 
                    who refuses to accept &ldquo;good enough.&rdquo;
                  </p>
                </div>
              </div>

              {/* Right - Visual */}
              <div className="relative">
                <div className="absolute -inset-4 bg-emerald-500/10 rounded-2xl blur-xl" />
                <div className="relative bg-slate-900/80 border border-emerald-500/30 rounded-2xl p-8 backdrop-blur">
                  {/* App preview */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 rounded-lg bg-emerald-500/20 border border-emerald-500/50 flex items-center justify-center">
                        <Crosshair className="h-5 w-5 text-emerald-400" />
                      </div>
                      <div>
                        <span className="font-bold text-white">CONTRACTOR OVERWATCH</span>
                        <p className="text-xs text-slate-500">Unified Operating System</p>
                      </div>
                    </div>
                    
                    {[
                      { label: "Leads Tracked", value: "100%", color: "emerald" },
                      { label: "Estimate Accuracy", value: "+89%", color: "emerald" },
                      { label: "Revenue Growth", value: "$315K", color: "emerald" },
                      { label: "Time Saved Weekly", value: "10+ hrs", color: "emerald" },
                    ].map((stat, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-slate-950/50 rounded-lg border border-slate-800">
                        <span className="text-sm text-slate-400">{stat.label}</span>
                        <span className={`font-bold font-mono text-${stat.color}-400`}>{stat.value}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 text-xs text-slate-500 font-mono text-center">
                    STATUS: <span className="text-emerald-400">MISSION READY</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-emerald-950/10 to-slate-950" />
        
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          {/* Decorative element */}
          <div className="mb-8 flex items-center justify-center gap-4">
            <div className="h-px w-24 bg-gradient-to-r from-transparent to-emerald-500/50" />
            <Shield className="h-8 w-8 text-emerald-500" />
            <div className="h-px w-24 bg-gradient-to-l from-transparent to-emerald-500/50" />
          </div>

          <h2 className="text-3xl sm:text-4xl font-black text-white mb-6" style={{ fontFamily: "'Courier New', Courier, monospace" }}>
            READY TO JOIN THE MISSION?
          </h2>
          
          <p className="text-lg text-slate-400 mb-10 max-w-xl mx-auto">
            Stop running your business like a civilian. Take command with Contractor Overwatch.
          </p>

          <Link to={isLoggedIn ? "/dashboard" : "/login"}>
            <Button size="lg" className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-lg px-10 py-7 shadow-lg shadow-emerald-900/50 group">
              REPORT FOR DUTY
              <ArrowRight className="ml-3 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>

          <p className="mt-6 text-sm text-slate-500">
            Free access for founding members. No credit card required.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-950 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back to Base Link */}
          <div className="flex justify-center mb-8">
            <a 
              href="/marketing#features" 
              className="group inline-flex items-center gap-2 text-slate-400 hover:text-emerald-400 transition-colors"
            >
              <ChevronRight className="h-4 w-4 rotate-180 group-hover:-translate-x-1 transition-transform" />
              <span className="text-sm font-medium">RETURN TO FEATURES</span>
            </a>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Link to="/marketing" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/20 border border-emerald-500/50 flex items-center justify-center">
                <Crosshair className="h-4 w-4 text-emerald-400" />
              </div>
              <span className="font-bold text-slate-400">CONTRACTOR <span className="text-emerald-400">OVERWATCH</span></span>
            </Link>
            
            <div className="flex items-center gap-6 text-sm text-slate-500">
              <Link to="/marketing" className="hover:text-emerald-400 transition-colors">Home</Link>
              <a href="/marketing#features" className="hover:text-emerald-400 transition-colors">Features</a>
              <a href="/marketing#benefits" className="hover:text-emerald-400 transition-colors">Benefits</a>
              <Link to="/about" className="text-emerald-400">About</Link>
            </div>

            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Flag className="h-4 w-4 text-red-500" />
              <span>Veteran Owned & Operated</span>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-slate-800/50 text-center">
            <p className="text-xs text-slate-600">
              © {new Date().getFullYear()} Contractor Overwatch. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
